import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
const API = process.env.REACT_APP_API_URL;

const AVATAR_COLORS = [
  '#25D366','#128C7E','#075E54','#34B7F1',
  '#8B5CF6','#EC4899','#F59E0B','#EF4444',
  '#3B82F6','#10B981',
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

/* ── Spinner ── */
const Spinner = ({ size = 'w-4 h-4', color = 'text-white' }) => (
  <svg className={`animate-spin ${size} ${color}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

/* ── Stat Card ── */
const StatCard = ({ value, label, icon, gradient }) => (
  <div className={`relative overflow-hidden rounded-xl sm:rounded-2xl p-3 sm:p-5 text-white shadow-lg ${gradient}`}>
    <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white bg-opacity-10" />
    <div className="absolute -bottom-4 -right-1 w-24 h-24 rounded-full bg-white bg-opacity-5" />
    <div className="relative">
      <div className="text-2xl sm:text-4xl font-black mb-0.5">{value}</div>
      <div className="text-xs sm:text-sm font-medium opacity-80 flex items-center gap-1">
        <span>{icon}</span><span>{label}</span>
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { token, user } = useAuth();
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [formData, setFormData]         = useState({ name: '', username: '', password: '', role: 'student' });
  const [formError, setFormError]       = useState('');
  const [formSuccess, setFormSuccess]   = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [deletingId, setDeletingId]     = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm]     = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview]   = useState(null);
  const [filterRole, setFilterRole]     = useState('all');
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [countdown, setCountdown]           = useState(30);
  const fileInputRef  = useRef(null);
  const intervalRef   = useRef(null);
  const countdownRef  = useRef(null);
  const REFRESH_SECS  = 30;

  const fetchUsers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setAutoRefreshing(true);
    try {
      const res = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
      setAutoRefreshing(false);
    }
  }, [token]);

  // Initial load
  useEffect(() => { fetchUsers(false); }, [fetchUsers]);

  // Auto-refresh every REFRESH_SECS seconds (pauses during mutations)
  const isBusy = useMemo(() => submitting || deletingId !== null, [submitting, deletingId]);

  useEffect(() => {
    // Reset countdown whenever the effect re-runs
    setCountdown(REFRESH_SECS);

    // Tick countdown every second
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_SECS : prev - 1));
    }, 1000);

    // Fetch every REFRESH_SECS seconds (skip if a mutation is in flight)
    intervalRef.current = setInterval(() => {
      if (!isBusy) {
        fetchUsers(true);
        setCountdown(REFRESH_SECS);
      }
    }, REFRESH_SECS * 1000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [fetchUsers, isBusy]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
    e.target.value = '';
  };

  const clearFile = () => { setSelectedFile(null); setFilePreview(null); };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (!formData.name.trim() || !formData.username.trim() || !formData.password.trim()) {
      setFormError('All fields are required.'); return;
    }
    if (formData.password.length < 4) {
      setFormError('Password must be at least 4 characters.'); return;
    }
    setSubmitting(true);
    try {
      let res;
      if (selectedFile) {
        const data = new FormData();
        Object.entries(formData).forEach(([k, v]) => data.append(k, v));
        data.append('avatar', selectedFile);
        res = await api.post('/api/users', data, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await axios.post(`${API}/api/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setUsers(prev => [res.data, ...prev]);
      setFormData({ name: '', username: '', password: '', role: 'student' });
      clearFile();
      setFormSuccess(`User "${res.data.name}" created successfully!`);
      setTimeout(() => setFormSuccess(''), 4000);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Error creating user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete "${userName}"? This cannot be undone.`)) return;
    setDeletingId(userId);
    try {
      await axios.delete(`${API}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting user.');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch =
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const studentCount = users.filter(u => u.role === 'student').length;
  const adminCount   = users.filter(u => u.role === 'admin').length;

  /* ── Input field helper ── */
  const inputCls = "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-whatsapp-green focus:border-transparent text-sm text-gray-800 placeholder-gray-400 transition-all duration-200 disabled:opacity-50";

  /* ── Render ── */
  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 px-3 sm:px-4">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-whatsapp-darker">⚙️ Admin Dashboard</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
              Welcome back, <span className="font-semibold text-whatsapp-dark">{user?.name}</span>
            </p>
          </div>
          <button
            onClick={() => { fetchUsers(false); setCountdown(REFRESH_SECS); }}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-whatsapp-green transition-all shadow-sm text-xs font-medium"
            title="Refresh now"
          >
            <svg
              className={`w-4 h-4 ${autoRefreshing ? 'animate-spin text-whatsapp-green' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline text-gray-400">
              {autoRefreshing ? 'Refreshing…' : `${countdown}s`}
            </span>
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard value={users.length}  label="Total Users"  icon="👥" gradient="bg-gradient-to-br from-whatsapp-green to-whatsapp-dark" />
          <StatCard value={studentCount}  label="Students"     icon="🎓" gradient="bg-gradient-to-br from-blue-500 to-blue-700" />
          <StatCard value={adminCount}    label="Admins"       icon="🛡️" gradient="bg-gradient-to-br from-purple-500 to-purple-700" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* ── Add User Form ── */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-7 h-7 bg-whatsapp-green rounded-lg flex items-center justify-center text-white text-sm">+</span>
                Add New User
              </h2>

              <form onSubmit={handleAddUser} className="space-y-3">
                {/* Full Name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label>
                  <input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    className={inputCls}
                    disabled={submitting}
                  />
                </div>

                {/* Username */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Username</label>
                  <input
                    type="text"
                    placeholder="e.g. johndoe"
                    value={formData.username}
                    onChange={e => setFormData(p => ({ ...p, username: e.target.value }))}
                    className={inputCls}
                    disabled={submitting}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 4 characters"
                      value={formData.password}
                      onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                      className={inputCls + ' pr-10'}
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                    className={inputCls}
                    disabled={submitting}
                  >
                    <option value="student">Student</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Avatar color preview */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Avatar Color (auto-assigned)</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {AVATAR_COLORS.map(c => (
                      <div key={c} className="w-6 h-6 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>

                {/* Optional profile picture */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Profile Picture (optional)</label>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                  {filePreview ? (
                    <div className="flex items-center gap-2">
                      <img src={filePreview} alt="preview" className="w-12 h-12 rounded-full object-cover border-2 border-whatsapp-green" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-600 truncate">{selectedFile?.name}</p>
                        <button type="button" onClick={clearFile} className="text-xs text-red-500 hover:underline">Remove</button>
                      </div>
                    </div>
                  ) : selectedFile ? (
                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200">
                      <span className="text-lg">📎</span>
                      <p className="text-xs text-gray-600 truncate flex-1">{selectedFile.name}</p>
                      <button type="button" onClick={clearFile} className="text-xs text-red-500 hover:underline">Remove</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-200 rounded-xl py-3 text-xs text-gray-400 hover:border-whatsapp-green hover:text-whatsapp-green transition-colors"
                    >
                      📷 Click to upload image
                    </button>
                  )}
                </div>

                {/* Feedback messages */}
                {formError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                    <span className="text-red-500 text-sm mt-0.5">⚠️</span>
                    <p className="text-xs text-red-600">{formError}</p>
                  </div>
                )}
                {formSuccess && (
                  <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
                    <span className="text-green-500 text-sm mt-0.5">✅</span>
                    <p className="text-xs text-green-700">{formSuccess}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-whatsapp-green hover:bg-whatsapp-dark text-white py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2 shadow-md"
                >
                  {submitting ? (
                    <><Spinner /><span>Creating...</span></>
                  ) : (
                    <><span>➕</span><span>Create User</span></>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* ── Users List ── */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* List header */}
              <div className="p-4 border-b border-gray-100 space-y-3">
                <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <span>👥</span> Users
                  <span className="ml-auto text-xs font-normal text-gray-400">{filteredUsers.length} shown</span>
                </h2>

                {/* Search */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or username…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                  />
                </div>

                {/* Role filter */}
                <div className="flex gap-2">
                  {['all', 'student', 'admin'].map(r => (
                    <button
                      key={r}
                      onClick={() => setFilterRole(r)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                        filterRole === r
                          ? 'bg-whatsapp-green text-white shadow-sm'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* List body */}
              <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Spinner size="w-8 h-8" color="text-whatsapp-green" />
                    <p className="text-sm text-gray-400">Loading users…</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="text-5xl mb-3">🔍</div>
                    <p className="text-gray-500 font-medium">No users found</p>
                    <p className="text-gray-400 text-sm mt-1">Try a different search or filter</p>
                  </div>
                ) : (
                  filteredUsers.map(u => (
                    <div key={u.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                      {/* Avatar */}
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm"
                        style={{ backgroundColor: u.avatar_color || '#25D366' }}
                      >
                        {getInitials(u.name)}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-800 text-sm truncate">{u.name}</p>
                          {u.id === user?.id && (
                            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">You</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate">@{u.username}</p>
                      </div>

                      {/* Role badge */}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                        u.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {u.role}
                      </span>

                      {/* Delete button */}
                      {u.id !== user?.id && (
                        <button
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          disabled={deletingId === u.id}
                          className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                          title={`Delete ${u.name}`}
                        >
                          {deletingId === u.id ? (
                            <Spinner size="w-4 h-4" color="text-red-400" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
